import type { BulletedListData, Decoration, OrderedListData, ParagraphData } from 'ricos-schema';
import { Decoration_Type, FontSizeData_fontType, Link_Target, Node_Type } from 'ricos-schema';

import type { NodeBuilder, TextNodeBuilder } from './types';
import { isDefined } from './utils';

export const p = (...args: (string | TextNodeBuilder | ParagraphData)[]): NodeBuilder => {
  const paragraphData = args.find(
    (a): a is ParagraphData => typeof a !== 'string' && !('type' in a)
  );
  const nodes = args
    .map(arg =>
      typeof arg === 'string'
        ? text(arg).node()
        : 'type' in arg && arg.type === 'text_node'
        ? arg.node()
        : undefined
    )
    .filter(isDefined);
  return {
    type: 'block_node',
    // @ts-ignore
    node: getId => {
      return {
        id: getId(),
        type: Node_Type.PARAGRAPH,
        ...(paragraphData ? { paragraphData } : {}),
        ...(nodes?.length && { nodes }),
      };
    },
  };
};

export const ul = (...args: (NodeBuilder | BulletedListData)[]): NodeBuilder => {
  const bulletedListData = args.find((a): a is BulletedListData => !('type' in a));

  return {
    type: 'block_node',
    node: getId => ({
      id: getId(),
      type: Node_Type.BULLETED_LIST,
      nodes: args
        .map(arg => {
          const nodeArg = arg as NodeBuilder;
          return nodeArg.node ? nodeArg.node(getId) : undefined;
        })
        .filter(isDefined),
      ...(bulletedListData !== undefined && { bulletedListData }),
    }),
  };
};

export const ol = (...args: (NodeBuilder | OrderedListData)[]): NodeBuilder => {
  const orderedListData = args.find((a): a is OrderedListData => !('type' in a));

  return {
    type: 'block_node',
    node: getId => ({
      id: getId(),
      type: Node_Type.ORDERED_LIST,
      nodes: args
        .map(arg => {
          const nodeArg = arg as NodeBuilder;
          return nodeArg.node ? nodeArg.node(getId) : undefined;
        })
        .filter(isDefined),
      ...(orderedListData !== undefined && { orderedListData }),
    }),
  };
};

export const li = (...args: NodeBuilder[]): NodeBuilder => {
  return {
    type: 'block_node',
    node: getId => ({
      id: getId(),
      type: Node_Type.LIST_ITEM,
      nodes: args.map(arg => arg.node(getId)),
    }),
  };
};
export const text = (text: string, ...decorations: Decoration[]): TextNodeBuilder => {
  return {
    type: 'text_node',
    node: () => ({
      type: Node_Type.TEXT,
      id: '',
      // @ts-expect-error
      textData: { text, ...(decorations.length ? { decorations } : {}) },
    }),
  };
};

export const fontSize = (fontSize: number): Decoration => {
  return {
    type: Decoration_Type.FONT_SIZE,
    fontSizeData: { unit: FontSizeData_fontType.PX, value: fontSize },
  };
};

export const blockquote = (...args: NodeBuilder[]): NodeBuilder => {
  return {
    type: 'block_node',
    node: getId => ({
      id: getId(),
      type: Node_Type.BLOCKQUOTE,
      nodes: args.map(arg => arg.node(getId)),
      blockquoteData: {
        indentation: 0,
      },
    }),
  };
};

export const italic = (italicData = true): Decoration => ({
  type: Decoration_Type.ITALIC,
  italicData,
});

export const link = (url: string): Decoration => ({
  type: Decoration_Type.LINK,
  linkData: {
    link: {
      anchor: undefined,
      target: Link_Target.BLANK,
      url,
    },
  },
});

export const textColor = ({
  background = '',
  foreground = '',
}: {
  background?: string;
  foreground?: string;
}): Decoration => ({
  type: Decoration_Type.COLOR,
  colorData: {
    background,
    foreground,
  },
});
